import { BANK_DEFAULT_COUNTRY } from "./drawRequest.constants";
import { DrawStage, publicClient } from "@toorak/tc-common-fe-sdk";
import Cookies from "js-cookie";
import { getConfig } from "../../config/config";
import {
  showLoader,
  hideLoader
} from "../loaderAndException/loaderAndException.action";
import { getCookie } from "../../utils/cookies";
import { downloadExportedTape } from "../../create-loan/create-loan.action";

import { sanitizeCurrency } from "../../utils/formatChecks";
import {
  formatBudget,
  getBudgetReviewPropType
} from "../../utils/budgetReconcileUtils";
import { getRoles, isOrigUser } from "../../utils/AccessManagement";
import { DrawInternalNotesInterface, DrawObj } from "./drawRequest.interface";
import {
  getUsername,
  sanitizeStringToNum,
  resolveDrawDocumentTaggingStatus
} from "../../assetManagement/AssetManagement.utils";
import { apiUrlHost } from "../servicerOnboarding/servicerOnboarding.action";
import {
  getDocsByDrawId,
  initBankObj,
  setFieldsHistory,
  updateAssigneeList,
  updateBankList,
  updateBudgetReviewCommentsRedux,
  updateBudgetReconcileRedux,
  updateBudgetReconcileReportsRedux,
  updateDrawAvalBank,
  updateDrawCount,
  updateDrawData,
  updateDrawDiscussions,
  updateDrawHistory,
  updateDrawInternalNotes,
  updateDrawObj,
  updateDrawPropOptions,
  updateDrawSelectedProp,
  updateDrawStageHistory,
  updateElasticSearchBody,
  updateFinalApproval,
  updateBudgetReconcileCommentsRedux,
  updateInspectionReviewRedux,
  setBudgetReviewPropType,
  updateLoanIds,
  updateProvisionData,
  updateReconcileItemHistoryRedux,
  updateSelectedBankObj
} from "./drawRequest.reducer";
import { getChatMeta } from "../commentsSection/comment.action";
import { getPartyId } from "../WaiverRequest/WaiverRequest.action";
import { postLineComment } from "../budgetReview/budgetReview.action";
import { createCommentThread1 } from "../Discussions/Discussions.action";
import { sortArrayByUpdatedOn } from "../../budget-review/BudgetReview.utils";
import { taskNames } from "../tasks/Tasks.action";
import { getTaskDetails } from "../../network/apiService";
import { updatePurchaseFlags } from "../purchasedLoansDocReview/purchasedLoansDocReview.action";
import {
  isInspectionReviewStarted,
  resolveAutoPrimaryInspectionDocs
} from "../../assetManagement/DrawRequest/primaryInspectionDocUtils";
import { calculateNetAmount } from "../../assetManagement/DrawRequest/DrawDataLeftPanel/DrawAmountDetails";
import { isDrawAccessDeniedError } from "../../assetManagement/DrawRequest/drawAccessUtils";
import { getErrorConfigObject } from "../../ErrorHandler/ErrorConfigData";

/** True only when bankDetails represents a bank saved against THIS draw.
 * An array-shaped bankDetails is the originator's Account List (the dropdown
 * source), not a saved selection for this draw, so it must not suppress the
 * bank that came from an Excel upload.
 */
export function hasSavedBankOnDraw(bankDetails: any): boolean {
  if (!bankDetails || Array.isArray(bankDetails)) return false;
  return !!(bankDetails.accountNumber ?? bankDetails.routing);
}

function normalizeLoanIdForCompare(loanId: string | null | undefined): string {
  if (loanId == null) return "";
  const asString = String(loanId);
  return asString.endsWith("#") ? asString.slice(0, -1) : asString;
}

/**
 * Draft Excel/draw bank should be preserved when loan-details is for the same
 * loan currently on the draw. Match by primaryLoanId OR toorakLoanId so the
 * v2 path (URL uses toorakLoanId) still preserves Excel banks. When the user
 * switches loans, primary/toorak on the draw no longer match the response and
 * preserve stays false so Account List defaults can apply.
 */
export function shouldPreserveDraftLoanDetails(params: {
  stage?: string | null;
  loanIdParam?: string | null;
  drawPrimaryLoanId?: string | null;
  drawToorakLoanId?: string | null;
  responsePrimaryLoanId?: string | null;
  responseToorakLoanId?: string | null;
}): boolean {
  const {
    stage,
    loanIdParam,
    drawPrimaryLoanId,
    drawToorakLoanId,
    responsePrimaryLoanId,
    responseToorakLoanId
  } = params;
  if (stage !== DrawStage.draft) return false;

  const param = normalizeLoanIdForCompare(loanIdParam);
  const drawPrimary = normalizeLoanIdForCompare(drawPrimaryLoanId);
  const drawToorak = normalizeLoanIdForCompare(drawToorakLoanId);
  const responsePrimary = normalizeLoanIdForCompare(responsePrimaryLoanId);
  const responseToorak = normalizeLoanIdForCompare(responseToorakLoanId);

  // Same loan currently on the draw as the loan-details response.
  const samePrimary =
    !!drawPrimary && !!responsePrimary && drawPrimary === responsePrimary;
  const sameToorak =
    !!drawToorak && !!responseToorak && drawToorak === responseToorak;
  // Legacy: caller still passes primaryLoanId as the loan id param.
  const paramMatchesPrimary =
    !!param && !!drawPrimary && param === drawPrimary;
  // v2: caller passes toorakLoanId; response confirms that toorak id.
  const paramMatchesResponseToorak =
    !!param && !!responseToorak && param === responseToorak && samePrimary;

  return samePrimary || sameToorak || paramMatchesPrimary || paramMatchesResponseToorak;
}

/** Build a selectable bank object from an Excel upload row. */
export function bankDetailsFromExcelRow(excelRow: any) {
  if (!excelRow) return null;
  const bankFromExcel =
    excelRow.bankDetails &&
    (Array.isArray(excelRow.bankDetails) ? excelRow.bankDetails[0] : excelRow.bankDetails);
  const hasExcelBank =
    bankFromExcel?.accountNumber ??
    bankFromExcel?.routing ??
    excelRow.accountNumber ??
    excelRow.routing;
  if (!hasExcelBank) return null;
  const b = bankFromExcel ?? excelRow;
  return {
    accountNumber: b.accountNumber ?? excelRow.accountNumber ?? 0,
    routing: b.routing ?? excelRow.routing ?? "",
    bankName: b.bankName ?? excelRow.bankName ?? "",
    accountName: b.accountName ?? excelRow.accountName ?? "",
    bankDetailId: b.bankDetailId || 0,
    city: b.city ?? "",
    state: b.state ?? "",
    zipcode: b.zipcode ?? "",
    country: b.country ?? BANK_DEFAULT_COUNTRY
  };
}

/** Choose default bank:
 * 0) Prefer bank marked preferredForNewDraw (latest Wire Transfer Initiated / Wire Completed)
 * 1) If any items have drawNumber, pick highest drawNumber
 * 2) Else if any items have requestedDate, pick latest requestedDate
 * 3) Else if any items have bankDetailId, pick highest bankDetailId
 * 4) Else fall back to first item
 */
export function pickDefaultBank(list: any[]): any {
  if (!list || list.length === 0) return undefined;

  const preferred = list.find((b: any) => b?.preferredForNewDraw === true);
  if (preferred) return preferred;

  const hasNonEmpty = (val: any) =>
    val !== null && val !== undefined && String(val).trim() !== "";

  const toNumeric = (val: any): number => {
    if (!hasNonEmpty(val)) return Number.NaN;
    const n = Number(String(val).replace(/[^0-9.-]/g, ""));
    return Number.isNaN(n) ? Number.NaN : n;
  };

  const withDrawNumber = list.filter((b: any) => !Number.isNaN(toNumeric(b?.drawNumber)));
  if (withDrawNumber.length > 0) {
    return withDrawNumber.reduce((best: any, b: any) => {
      const bVal = toNumeric(b?.drawNumber);
      const bestVal = toNumeric(best?.drawNumber);
      return Number.isNaN(bestVal) || bVal > bestVal ? b : best;
    }, withDrawNumber[0]);
  }

  const withRequestedDate = list.filter(
    (b: any) =>
      hasNonEmpty(b?.requestedDate) &&
      !Number.isNaN(new Date(b.requestedDate).getTime())
  );
  if (withRequestedDate.length > 0) {
    return withRequestedDate.reduce((best: any, b: any) => {
      const bVal = new Date(b.requestedDate).getTime();
      const bestVal = new Date(best?.requestedDate).getTime();
      return Number.isNaN(bestVal) || bVal > bestVal ? b : best;
    }, withRequestedDate[0]);
  }

  const withBankDetailId = list.filter((b: any) => hasNonEmpty(b?.bankDetailId));
  if (withBankDetailId.length > 0) {
    return withBankDetailId.reduce((best: any, b: any) => {
      const bVal = Number(b.bankDetailId);
      const bestVal = Number(best?.bankDetailId);
      return !Number.isNaN(bVal) && (Number.isNaN(bestVal) || bVal > bestVal) ? b : best;
    }, withBankDetailId[0]);
  }

  return list[0];
}

const DRAW_EXCEL_SAVED_IDS_KEY = "drawExcelSavedDrawIds";
const DRAW_SAVED_BANK_KEY_PREFIX = "drawSavedBank_";

/** Preserve decrypt metadata from API when session-stored bank selection omits it.
 * Only merge when account/routing match — otherwise Account List[0] metadata
 * (e.g. prior draw bankDetailId) would attach to an Excel-uploaded bank.
 */
function mergeBankDecryptMetadata(selected: any, apiBank: any) {
  if (!selected || !apiBank || typeof apiBank !== "object") return selected;
  const sameAccount =
    String(selected?.accountNumber ?? "") === String(apiBank?.accountNumber ?? "") &&
    String(selected?.routing ?? "") === String(apiBank?.routing ?? "");
  if (!sameAccount) return selected;
  return {
    ...selected,
    bankDetailId:
      selected.bankDetailId != null
      && selected.bankDetailId !== ""
      && selected.bankDetailId !== 0
        ? selected.bankDetailId
        : apiBank.bankDetailId ?? selected.bankDetailId,
    accountNumberEncryptionId:
      selected.accountNumberEncryptionId ?? apiBank.accountNumberEncryptionId,
    routingEncryptionId:
      selected.routingEncryptionId ?? apiBank.routingEncryptionId,
    accountNameEncryptionId:
      selected.accountNameEncryptionId ?? apiBank.accountNameEncryptionId,
    isEncrypted: selected.isEncrypted ?? apiBank.isEncrypted,
    needsDrawIdDecrypt: selected.needsDrawIdDecrypt ?? apiBank.needsDrawIdDecrypt
  };
}

function isDrawIdMarkedAsSaved(drawId: string): boolean {
  try {
    for (const storage of [sessionStorage, localStorage]) {
      const raw = storage.getItem(DRAW_EXCEL_SAVED_IDS_KEY);
      const ids: string[] = raw ? JSON.parse(raw) : [];
      if (ids.includes(String(drawId))) return true;
    }
  } catch {
    // Ignore storage parse errors; treat as not saved.
  }
  return false;
}

function getExcelRowForDraw(
  drawId: string,
  getState: any
): any {
  if (isDrawIdMarkedAsSaved(drawId)) return null;
  try {
    const savedRaw = sessionStorage.getItem(DRAW_EXCEL_SAVED_IDS_KEY);
    const savedIds: string[] = savedRaw ? JSON.parse(savedRaw) : [];
    if (savedIds.includes(String(drawId))) return null;
    const localRaw = localStorage.getItem(DRAW_EXCEL_SAVED_IDS_KEY);
    const localIds: string[] = localRaw ? JSON.parse(localRaw) : [];
    if (localIds.includes(String(drawId))) return null;
  } catch {
    // Ignore storage parse errors; fall through to Redux/session Excel rows.
  }
  const state = getState?.();
  let rows: any[] = state?.drawRequestStore?.excelData ?? [];
  if (!rows?.length) {
    try {
      const stored = sessionStorage.getItem("drawExcelUpload");
      if (stored) rows = JSON.parse(stored);
    } catch {
      // Ignore invalid drawExcelUpload JSON.
    }
  }
  if (!rows?.length) return null;
  return rows.find(
    (row: any) => String(row?.drawId) === String(drawId)
  ) ?? null;
}

function mergeExcelRowIntoDrawResponse(
  response: any,
  excelRow: any,
  drawId: string
): void {
  if (!excelRow || !response) return;
  // Skip only when the draw already has both a saved amount and an object-shaped
  // bank. An array-shaped bankDetails is the Account List dropdown source and must
  // not suppress Excel amount/bank merge after BulkDrawUpload.
  const apiHasAmount =
    response.drawAmountDetails?.drawAmount != null &&
    String(response.drawAmountDetails.drawAmount).trim() !== "" &&
    Number(response.drawAmountDetails.drawAmount) !== 0;
  const apiHasBank = hasSavedBankOnDraw(response.bankDetails);
  if (apiHasAmount && apiHasBank) return;

  const emptyAmount =
    !response.drawAmountDetails?.drawAmount ||
    String(response.drawAmountDetails.drawAmount).trim() === "" ||
    Number(response.drawAmountDetails.drawAmount) === 0;
  // Support multiple possible keys from Excel/upload API (drawAmount, drawAmountDetails.drawAmount, requestedAmount, amount)
  const excelAmount =
    excelRow.drawAmountDetails?.drawAmount ??
    excelRow.drawAmount ??
    excelRow.requestedAmount ??
    excelRow.amount;
  if (emptyAmount && (excelAmount !== undefined && excelAmount !== null && String(excelAmount).trim() !== "")) {
    response.drawAmountDetails = response.drawAmountDetails ?? {};
    const src = excelRow.drawAmountDetails ?? excelRow;
    response.drawAmountDetails.drawAmount =
      src.drawAmount ??
      excelRow.drawAmount ??
      excelRow.requestedAmount ??
      excelRow.amount ??
      response.drawAmountDetails.drawAmount;
    response.drawAmountDetails.wireFee = src.wireFee ?? response.drawAmountDetails.wireFee ?? "0.00";
    response.drawAmountDetails.costOfInspection = src.costOfInspection ?? response.drawAmountDetails.costOfInspection ?? "0.00";
    response.drawAmountDetails.titleServiceFee = src.titleServiceFee ?? response.drawAmountDetails.titleServiceFee ?? "0.00";
    response.drawAmountDetails.wireReturnFee = src.wireReturnFee ?? response.drawAmountDetails.wireReturnFee ?? "0.00";
    if (response.stage === DrawStage.draft && response.drawAmountDetails) {
      response.drawAmountDetails.netFundsToBorrower = calculateNetAmount(
        response.drawAmountDetails
      );
    }
  }

  const hasResponseBank = hasSavedBankOnDraw(response.bankDetails);
  const bankFromExcel = bankDetailsFromExcelRow(excelRow);
  // Only use Excel bank when API has no saved bank (e.g. first load after Excel create). After user saves and reloads, prefer API.
  if (bankFromExcel && !hasResponseBank) {
    response.bankDetails = bankFromExcel;
    try {
      sessionStorage.setItem(`drawExcelBank_${drawId}`, JSON.stringify(bankFromExcel));
    } catch {
      // sessionStorage may be unavailable (private mode / quota).
    }
  }
}

export function captureBankListFromLoanDetailsResponse(bankDetails: any): any[] {
  if (Array.isArray(bankDetails)) {
    return [...bankDetails];
  }
  if (bankDetails && typeof bankDetails === "object") {
    return [bankDetails];
  }
  return [];
}

function resolveBankPayloadFromResponse(bankDetails: any) {
  if (bankDetails == null) return initBankObj;
  if (!Array.isArray(bankDetails)) return bankDetails;
  if (bankDetails.length === 0) return initBankObj;
  return pickDefaultBank(bankDetails) || bankDetails[0];
}

export function hasNonZeroDrawAmount(amountDetails: any): boolean {
  const amount = amountDetails?.drawAmount;
  if (amount == null) return false;
  if (String(amount).trim() === "") return false;
  return Number(amount) !== 0;
}

export function hasBankAccountOrRouting(bank: any): boolean {
  if (!bank) return false;
  const hasAccount =
    bank.accountNumber != null &&
    bank.accountNumber !== "" &&
    bank.accountNumber !== 0;
  const hasRouting =
    bank.routing != null && String(bank.routing).trim() !== "";
  return hasAccount || hasRouting;
}

export function isResponseBankEmpty(bankDetails: any): boolean {
  if (!bankDetails) return true;
  if (Array.isArray(bankDetails)) {
    return (
      bankDetails.length === 0 ||
      !(bankDetails[0]?.accountNumber ?? bankDetails[0]?.routing)
    );
  }
  return !(bankDetails?.accountNumber ?? bankDetails?.routing);
}

function isEmptyBankObj(bank: any): boolean {
  if (!bank || bank === initBankObj) return true;
  const emptyAccount =
    bank.accountNumber === 0 ||
    bank.accountNumber === "" ||
    bank.accountNumber == null;
  const emptyRouting = !bank.routing || String(bank.routing).trim() === "";
  return emptyAccount && emptyRouting;
}

function applyDraftNetFunds(response: any) {
  if (response.stage === DrawStage.draft && response.drawAmountDetails) {
    response.drawAmountDetails.netFundsToBorrower = calculateNetAmount(
      response.drawAmountDetails
    );
  }
}

function apiBankFromResponse(response: any) {
  return Array.isArray(response?.bankDetails)
    ? response.bankDetails[0]
    : response?.bankDetails;
}

function banksMatch(left: any, right: any): boolean {
  return (
    !!left &&
    String(left.accountNumber ?? "") === String(right?.accountNumber ?? "") &&
    String(left.routing ?? "") === String(right?.routing ?? "")
  );
}

function readSessionSavedBank(drawId: string) {
  try {
    const stored = sessionStorage.getItem(
      `${DRAW_SAVED_BANK_KEY_PREFIX}${drawId}`
    );
    if (!stored) return null;
    const savedBank = JSON.parse(stored);
    return hasBankAccountOrRouting(savedBank) ? savedBank : null;
  } catch {
    return null;
  }
}

function mergeSavedBankWithResponse(savedBank: any, response: any) {
  const list = captureBankListFromLoanDetailsResponse(response.bankDetails);
  const match = list.find((bank: any) => banksMatch(bank, savedBank));
  return mergeBankDecryptMetadata(match ?? savedBank, apiBankFromResponse(response));
}

function handleLoanDetailsErrorMessage(
  response: any,
  setOpenSnackBar: any,
  setSnackBarMsg: any
) {
  if (!response?.message) return;
  setOpenSnackBar(true);
  setSnackBarMsg({
    messageType: "error",
    message: response.message
  });
}

function overlayDrawAmountDetails(params: {
  response: any;
  drawAmountDetails: any;
  drawId: string;
  getState: any;
  shouldPreserve: boolean;
  currentState: any;
}) {
  const {
    response,
    drawAmountDetails,
    drawId,
    getState,
    shouldPreserve,
    currentState
  } = params;
  const hasPassedAmount =
    drawAmountDetails &&
    typeof drawAmountDetails === "object" &&
    Object.keys(drawAmountDetails).length > 0;
  if (hasPassedAmount) {
    response.drawAmountDetails = drawAmountDetails;
    return;
  }
  if (isDrawIdMarkedAsSaved(drawId)) return;
  const excelRow = getExcelRowForDraw(drawId, getState);
  if (excelRow) mergeExcelRowIntoDrawResponse(response, excelRow, drawId);
  if (!shouldPreserve) return;
  const existingAmount =
    currentState?.drawRequestStore?.drawDetails?.drawAmountDetails;
  if (
    hasNonZeroDrawAmount(response.drawAmountDetails) ||
    !hasNonZeroDrawAmount(existingAmount)
  ) {
    return;
  }
  response.drawAmountDetails = {
    ...response.drawAmountDetails,
    ...existingAmount
  };
  applyDraftNetFunds(response);
}

function preserveDraftLoanFields(
  response: any,
  drawObj: any,
  shouldPreserve: boolean
) {
  if (!drawObj || !shouldPreserve) return;
  if (!response.gracePeriod && drawObj.gracePeriod) {
    response.gracePeriod = drawObj.gracePeriod;
  }
  if (!response.payOffRequestDate && drawObj.payOffRequestDate) {
    response.payOffRequestDate = drawObj.payOffRequestDate;
  }
  if (!response.loanStatus && drawObj.loanStatus) {
    response.loanStatus = drawObj.loanStatus;
  }
}

function preservePropertyDetails(response: any, currentState: any) {
  const existingProp =
    currentState?.drawRequestStore?.drawDetails?.propertyDetails;
  if (existingProp?.length && !response?.propertyDetails?.length) {
    response.propertyDetails = existingProp;
  }
}

function preserveAmountAndBankFromStore(
  response: any,
  getState: any,
  currentState: any,
  drawId: string
) {
  const latestState = getState?.() ?? currentState;
  const existingDraw = latestState?.drawRequestStore?.drawDetails;
  const sameDraw =
    existingDraw &&
    String(existingDraw.drawId ?? existingDraw.id) === String(drawId);
  if (!sameDraw) return;
  const existingHasAmount = hasNonZeroDrawAmount(
    existingDraw?.drawAmountDetails
  );
  const responseHasEmptyAmount = !hasNonZeroDrawAmount(
    response.drawAmountDetails
  );
  const existingBank = latestState?.drawRequestStore?.selectedBankDetails;
  const existingHasBank = hasBankAccountOrRouting(existingBank);
  const responseHasEmptyBank = isResponseBankEmpty(response.bankDetails);
  if (existingHasAmount && responseHasEmptyAmount && existingDraw.drawAmountDetails) {
    response.drawAmountDetails = {
      ...response.drawAmountDetails,
      ...existingDraw.drawAmountDetails
    };
    applyDraftNetFunds(response);
  }
  if (existingHasBank && responseHasEmptyBank && existingBank) {
    response.bankDetails = existingBank;
  }
}

function selectBankFromAccountList(
  list: any[],
  existingBankFromDraw: any,
  hasExistingBank: boolean,
  excelBankForSelection: any
) {
  if (!list.length) return initBankObj;
  if (hasExistingBank) {
    const matchById = list.find(
      (bank: any) =>
        bank.bankDetailId != null &&
        String(bank.bankDetailId) === String(existingBankFromDraw.bankDetailId)
    );
    if (matchById) return matchById;
    const matchByAccountRouting = list.find((bank: any) =>
      banksMatch(bank, existingBankFromDraw)
    );
    if (matchByAccountRouting) return matchByAccountRouting;
    return { ...existingBankFromDraw };
  }
  if (excelBankForSelection) return excelBankForSelection;
  return pickDefaultBank(list) || list[0];
}

function bankFromDrawObj(bankDetails: any) {
  if (Array.isArray(bankDetails)) {
    return bankDetails.length > 0 ? bankDetails[0] : initBankObj;
  }
  if (typeof bankDetails === "object") return bankDetails;
  return initBankObj;
}

function applyPreserveBankSelection(params: {
  selectedBankObj: any;
  shouldPreserve: boolean;
  drawId: string;
  excelBankForSelection: any;
  existingBankFromDraw: any;
  hasExistingBank: boolean;
  drawObj: any;
}) {
  const {
    selectedBankObj,
    shouldPreserve,
    drawId,
    excelBankForSelection,
    existingBankFromDraw,
    hasExistingBank,
    drawObj
  } = params;
  if (shouldPreserve) {
    const bankFromExcel = isDrawIdMarkedAsSaved(drawId)
      ? null
      : excelBankForSelection;
    if (bankFromExcel) return bankFromExcel;
    if (hasExistingBank) return existingBankFromDraw;
    if (isEmptyBankObj(selectedBankObj) && drawObj?.bankDetails) {
      return bankFromDrawObj(drawObj.bankDetails);
    }
    return selectedBankObj;
  }
  if (isEmptyBankObj(selectedBankObj) && hasExistingBank) {
    return existingBankFromDraw;
  }
  return selectedBankObj;
}

function resolveSelectedBankForLoanDetails(params: {
  response: any;
  existingBankFromDraw: any;
  excelBankForSelection: any;
  shouldPreserve: boolean;
  drawId: string;
  drawObj: any;
}) {
  const {
    response,
    existingBankFromDraw,
    excelBankForSelection,
    shouldPreserve,
    drawId,
    drawObj
  } = params;
  const hasExistingBank =
    !!existingBankFromDraw &&
    existingBankFromDraw !== initBankObj &&
    hasBankAccountOrRouting(existingBankFromDraw);
  let selectedBankObj = initBankObj;
  if (response.bankDetails) {
    selectedBankObj = Array.isArray(response.bankDetails)
      ? selectBankFromAccountList(
          response.bankDetails,
          existingBankFromDraw,
          hasExistingBank,
          excelBankForSelection
        )
      : response.bankDetails;
  }
  selectedBankObj = applyPreserveBankSelection({
    selectedBankObj,
    shouldPreserve,
    drawId,
    excelBankForSelection,
    existingBankFromDraw,
    hasExistingBank,
    drawObj
  });
  if (!isDrawIdMarkedAsSaved(drawId)) return selectedBankObj;
  const savedBank = readSessionSavedBank(drawId);
  return savedBank
    ? mergeSavedBankWithResponse(savedBank, response)
    : selectedBankObj;
}

function applyChangedLoanDetails(params: {
  response: any;
  drawAmountDetails: any;
  drawId: string;
  getState: any;
  shouldPreserve: boolean;
  currentState: any;
  drawObj: any;
  firstLoadBankSnapshot: any;
  dispatch: any;
}) {
  const {
    response,
    drawAmountDetails,
    drawId,
    getState,
    shouldPreserve,
    currentState,
    drawObj,
    firstLoadBankSnapshot,
    dispatch
  } = params;
  overlayDrawAmountDetails({
    response,
    drawAmountDetails,
    drawId,
    getState,
    shouldPreserve,
    currentState
  });
  preserveDraftLoanFields(response, drawObj, shouldPreserve);
  preservePropertyDetails(response, currentState);
  preserveAmountAndBankFromStore(response, getState, currentState, drawId);
  response.drawId = response.drawId ?? drawId;
  dispatch(updateDrawObj(response));
  dispatch(updateDrawSelectedProp(response.propertyDetails ?? []));
  const excelBankForSelection = !isDrawIdMarkedAsSaved(drawId)
    ? bankDetailsFromExcelRow(getExcelRowForDraw(drawId, getState))
    : null;
  const selectedBankObj = resolveSelectedBankForLoanDetails({
    response,
    existingBankFromDraw:
      firstLoadBankSnapshot ?? currentState?.drawRequestStore?.selectedBankDetails,
    excelBankForSelection,
    shouldPreserve,
    drawId,
    drawObj
  });
  dispatch(
    updateSelectedBankObj(
      mergeBankDecryptMetadata(selectedBankObj, apiBankFromResponse(response))
    )
  );
}

function dispatchAvailableBanksAndProps(
  response: any,
  fullBankListFromApi: any[],
  shouldPreserve: boolean,
  currentState: any,
  dispatch: any
) {
  let listForDropdown =
    fullBankListFromApi.length > 0 ? fullBankListFromApi : [];
  if (listForDropdown.length === 0 && shouldPreserve) {
    const existingAvailableBanks =
      currentState?.drawRequestStore?.availableBankAccountDetails;
    if (Array.isArray(existingAvailableBanks) && existingAvailableBanks.length > 0) {
      listForDropdown = existingAvailableBanks;
    }
  }
  dispatch(
    updateDrawAvalBank({
      ...response,
      bankDetails: listForDropdown,
      bankList: response.bankList
    })
  );
  dispatch(updateDrawPropOptions(response.propertyDetails));
  if (response?.propertyDetails?.length === 1) {
    dispatch(updateDrawSelectedProp(response.propertyDetails));
  }
}

function mergeExcelAmountOntoDraw(excelRow: any, currentDraw: any) {
  const excelAmount =
    excelRow.drawAmountDetails?.drawAmount ??
    excelRow.drawAmount ??
    excelRow.requestedAmount ??
    excelRow.amount;
  if (
    excelAmount == null ||
    String(excelAmount).trim() === "" ||
    Number(excelAmount) === 0
  ) {
    return null;
  }
  const src = excelRow.drawAmountDetails ?? excelRow;
  const mergedAmount = {
    ...currentDraw?.drawAmountDetails,
    drawAmount:
      src.drawAmount ??
      excelRow.drawAmount ??
      excelRow.requestedAmount ??
      excelRow.amount,
    wireFee: src.wireFee ?? currentDraw?.drawAmountDetails?.wireFee ?? "0.00",
    costOfInspection:
      src.costOfInspection ??
      currentDraw?.drawAmountDetails?.costOfInspection ??
      "0.00",
    titleServiceFee:
      src.titleServiceFee ??
      currentDraw?.drawAmountDetails?.titleServiceFee ??
      "0.00",
    wireReturnFee:
      src.wireReturnFee ?? currentDraw?.drawAmountDetails?.wireReturnFee ?? "0.00"
  };
  mergedAmount.netFundsToBorrower = calculateNetAmount(mergedAmount);
  return mergedAmount;
}

function toSelectableBank(bankToApply: any) {
  return {
    accountNumber: bankToApply.accountNumber ?? 0,
    routing: bankToApply.routing ?? "",
    bankName: bankToApply.bankName ?? "",
    accountName: bankToApply.accountName ?? "",
    bankDetailId: bankToApply.bankDetailId || 0,
    city: bankToApply.city ?? "",
    state: bankToApply.state ?? "",
    zipcode: bankToApply.zipcode ?? "",
    country: bankToApply.country ?? BANK_DEFAULT_COUNTRY
  };
}

function reapplyExcelBankSelection(params: {
  drawAlreadySaved: boolean;
  excelBank: any;
  currentBank: any;
  drawId: string;
  firstLoadBankSnapshot: any;
  dispatch: any;
}) {
  const {
    drawAlreadySaved,
    excelBank,
    currentBank,
    drawId,
    firstLoadBankSnapshot,
    dispatch
  } = params;
  if (!drawAlreadySaved && excelBank) {
    if (!banksMatch(currentBank, excelBank)) {
      dispatch(updateSelectedBankObj({ ...excelBank }));
    }
    return;
  }
  if (excelBank) return;
  let bankToApply: any = null;
  try {
    const stored = sessionStorage.getItem(`drawExcelBank_${drawId}`);
    if (stored) bankToApply = JSON.parse(stored);
  } catch {
    // Ignore invalid drawExcelBank session JSON.
  }
  if (!bankToApply) bankToApply = firstLoadBankSnapshot;
  const hasBank =
    !!bankToApply &&
    bankToApply !== initBankObj &&
    hasBankAccountOrRouting(bankToApply);
  const hasSavedBank = hasBankAccountOrRouting(currentBank);
  if (hasBank && !hasSavedBank) {
    dispatch(updateSelectedBankObj(toSelectableBank(bankToApply)));
  }
}

function reapplyExcelAmountAndBank(
  drawId: string,
  getState: any,
  dispatch: any,
  firstLoadBankSnapshot: any
) {
  const stateAfter = getState();
  const currentDraw = stateAfter?.drawRequestStore?.drawDetails;
  const currentBank = stateAfter?.drawRequestStore?.selectedBankDetails;
  const excelRow = getExcelRowForDraw(drawId, getState);
  const excelBank = bankDetailsFromExcelRow(excelRow);
  if (excelRow && !hasNonZeroDrawAmount(currentDraw?.drawAmountDetails)) {
    const mergedAmount = mergeExcelAmountOntoDraw(excelRow, currentDraw);
    if (mergedAmount) {
      dispatch(
        updateDrawObj({ ...currentDraw, drawAmountDetails: mergedAmount })
      );
    }
  }
  reapplyExcelBankSelection({
    drawAlreadySaved: isDrawIdMarkedAsSaved(drawId),
    excelBank,
    currentBank,
    drawId,
    firstLoadBankSnapshot,
    dispatch
  });
}

function applyExcelBankIfMissing(response: any, excelRow: any, drawId: string) {
  const bank = bankDetailsFromExcelRow(excelRow);
  if (!bank || hasSavedBankOnDraw(response.bankDetails)) return;
  response.bankDetails = bank;
  try {
    sessionStorage.setItem(`drawExcelBank_${drawId}`, JSON.stringify(bank));
  } catch {
    // sessionStorage may be unavailable (private mode / quota).
  }
}

function applyExcelMergeToDrawDetailsResponse(
  response: any,
  drawId: string,
  getState: any
) {
  const draw =
    response.payload?.drawDetails ??
    response.payload ??
    response.drawDetails ??
    response;
  const skipExcelMerge =
    hasNonZeroDrawAmount(draw.drawAmountDetails ?? {}) ||
    hasSavedBankOnDraw(draw.bankDetails);
  if (skipExcelMerge) return;
  const excelRow = getExcelRowForDraw(drawId, getState);
  if (!excelRow) return;
  mergeExcelRowIntoDrawResponse(response, excelRow, drawId);
  if (
    draw !== response &&
    response.drawAmountDetails &&
    typeof draw === "object"
  ) {
    draw.drawAmountDetails = {
      ...draw.drawAmountDetails,
      ...response.drawAmountDetails
    };
  }
  applyExcelBankIfMissing(response, excelRow, drawId);
}

function resolveBankPayloadWithSession(response: any, drawId?: string) {
  let bankPayload = resolveBankPayloadFromResponse(response.bankDetails);
  if (!drawId) return bankPayload;
  const savedBank = readSessionSavedBank(drawId);
  if (savedBank) {
    bankPayload = mergeSavedBankWithResponse(savedBank, response);
  }
  return bankPayload;
}

function dispatchPropertyAndBankList(response: any, dispatch: any) {
  if (response?.propertyDetails?.length) {
    dispatch(updateDrawPropOptions(response.propertyDetails));
  }
  const bankList = response.bankList || response.bankDetails;
  if (Array.isArray(bankList) && bankList.length > 0) {
    dispatch(
      updateDrawAvalBank({
        ...response,
        bankDetails: bankList,
        bankList
      })
    );
  }
}

async function applyAutoPrimaryAndFilterDocs(
  response: any,
  isInspection?: boolean
) {
  const autoPrimary = resolveAutoPrimaryInspectionDocs<any>(
    response.drawDocuments,
    {
      getId: (doc: any) => doc?.documentId,
      getTags: (doc: any) => doc?.tags,
      inspectionReviewStarted: isInspectionReviewStarted(
        response?.budgetReconcileStatus
      )
    }
  );
  if (autoPrimary.flagUpdates.length) {
    try {
      await updatePurchaseFlags(autoPrimary.flagUpdates);
    } catch (err) {
      console.error(err);
    }
  }
  if (!isInspection) return autoPrimary.docs;
  return autoPrimary.docs?.filter(
    (item: any) =>
      item.isPurchasePrimary &&
      item.tags?.some(
        (tag: any) => tag.tag.code === "DDDC" || tag.tag.code === "ADRF"
      )
  );
}

async function fetchBudgetReviewStatusIfNeeded(
  Stage: DrawStage,
  drawObj: any,
  toorakLoanId: any
) {
  if (Stage === DrawStage.draft || drawObj.budgetReviewStatus !== "") {
    return "";
  }
  const payload = {
    resourceTypes: ["LOAN"],
    resourceIds: [toorakLoanId],
    taskNames: [taskNames.BUDGET_REVIEW]
  };
  const response: any = await getTaskDetails(payload);
  return response?.data?.[0]?.assignedTasks?.[0]?.taskStatus;
}

function buildDrawSaveObject(params: {
  drawObj: any;
  selectedBankDetails: any;
  Stage: DrawStage;
  isOrig: boolean;
  reviewStatus: string;
  recalculatedNetFunds: any;
  drawAmountDetails: any;
}) {
  const {
    drawObj,
    selectedBankDetails,
    Stage,
    isOrig,
    reviewStatus,
    recalculatedNetFunds,
    drawAmountDetails
  } = params;
  const {
    toorakLoanId,
    servicerName,
    servicerId,
    propertyDetails,
    prePurchaseDrawAmount,
    originatorId,
    loanType,
    drawType,
    financedBudgetAmount,
    drawNumber,
    balanceToFinish,
    borrowerAddress,
    borrowerEntityName,
    primaryLoanId,
    maturityDate,
    nextPaymentDueDate,
    lastUpdatedDate,
    originatorName: origName
  } = drawObj;
  const {
    costOfInspection,
    drawAmount,
    edited,
    netFundsToBorrower,
    titleServiceFee,
    wireFee,
    wireReturnFee,
    latestApprovedAmount,
    comments
  } = drawAmountDetails;
  const drawSaveObject: any = {
    balanceToFinish,
    toorakLoanId,
    propertyDetails,
    primaryLoanId,
    originatorId,
    originatorName: origName,
    loanExcelJson: "",
    maturityDate,
    nextPaymentDueDate,
    drawAmountDetails: {
      costOfInspection: sanitizeStringToNum(costOfInspection),
      drawAmount: sanitizeStringToNum(drawAmount),
      edited,
      netFundsToBorrower: sanitizeStringToNum(
        recalculatedNetFunds || netFundsToBorrower
      ),
      titleServiceFee: sanitizeStringToNum(titleServiceFee),
      wireFee: sanitizeStringToNum(wireFee),
      wireReturnFee: sanitizeStringToNum(wireReturnFee),
      latestApprovedAmount: sanitizeStringToNum(latestApprovedAmount),
      comments
    },
    bankDetails: selectedBankDetails || initBankObj,
    servicerId,
    servicerName,
    drawType,
    loanType,
    stage: Stage,
    drawNumber,
    borrowerEntityName,
    borrowerAddress,
    financedBudgetAmount,
    prePurchaseDrawAmount,
    availableFinancedBudgetAmount: null,
    lastUpdatedDate
  };
  if (!drawObj.losDrawId) {
    drawSaveObject.drawType = drawType;
  }
  if (!latestApprovedAmount || latestApprovedAmount === "0.00") {
    drawSaveObject.drawAmountDetails.latestApprovedAmount =
      sanitizeStringToNum(drawAmount);
  } else {
    drawSaveObject.drawAmountDetails.latestApprovedAmount =
      sanitizeStringToNum(latestApprovedAmount);
  }
  if (drawObj.budgetReviewStatus === "") {
    drawSaveObject.budgetReviewStatus = reviewStatus;
  }
  if (!isOrig) {
    drawSaveObject.username = getUsername();
  }
  if (Stage !== DrawStage.draft) {
    drawSaveObject.role = getRoles();
  }
  return drawSaveObject;
}

function assertNetFundsForSubmit(
  drawSaveObject: any,
  Stage: DrawStage,
  netFundsToBorrower: any,
  dispatch: any
) {
  if (
    Stage !== DrawStage.draft &&
    !(
      drawSaveObject.drawAmountDetails?.netFundsToBorrower &&
      drawSaveObject.drawAmountDetails?.drawAmount
    )
  ) {
    console.error(
      "Net funds to borrower is 0",
      drawSaveObject.drawAmountDetails,
      netFundsToBorrower
    );
    dispatch(hideLoader());
    throw new Error("Net funds to borrower is 0");
  }
}

async function refreshBankListAfterSave(params: {
  toorakLoanId: any;
  owner: any;
  isOrig: boolean;
  dispatch: any;
}) {
  const { toorakLoanId, owner, isOrig, dispatch } = params;
  try {
    const bankListUrl = `${apiUrlHost}/drawservice/assetmanagement/draws/loans/${toorakLoanId}/bankdetails?owner=${owner}&isOriginator=${isOrig}`;
    const bankListResponse = (await publicClient.get(bankListUrl)).data;
    if (!bankListResponse) return;
    const bankArray = Array.isArray(bankListResponse)
      ? bankListResponse
      : (bankListResponse.bankList ?? bankListResponse.bankDetails ?? []);
    if (bankArray.length > 0) {
      dispatch(
        updateDrawAvalBank({
          bankDetails: bankArray,
          bankList: bankArray
        })
      );
    }
  } catch (bankErr) {
    console.error("Failed to fetch updated bank list:", bankErr);
  }
}

function persistDraftSavedBank(drawId: string, bankToStore: any) {
  try {
    const key = "drawExcelSavedDrawIds";
    for (const storage of [sessionStorage, localStorage]) {
      const raw = storage.getItem(key);
      const ids: string[] = raw ? JSON.parse(raw) : [];
      if (!ids.includes(String(drawId))) {
        ids.push(String(drawId));
        storage.setItem(key, JSON.stringify(ids));
      }
    }
    if (bankToStore && (bankToStore.accountNumber != null || bankToStore.routing)) {
      try {
        sessionStorage.setItem(
          `${DRAW_SAVED_BANK_KEY_PREFIX}${drawId}`,
          JSON.stringify(bankToStore)
        );
        sessionStorage.removeItem(`drawExcelBank_${drawId}`);
      } catch {
        // sessionStorage may be unavailable (private mode / quota).
      }
    }
  } catch {
    // Ignore excel-saved-id bookkeeping failures.
  }
}

async function handleNonDraftDrawSave(params: {
  dispatch: any;
  response: any;
  isInspection?: boolean;
  selectedBankDetails: any;
  toorakLoanId: any;
  owner: any;
  isOrig: boolean;
}) {
  const {
    dispatch,
    response,
    isInspection,
    selectedBankDetails,
    toorakLoanId,
    owner,
    isOrig
  } = params;
  await dispatch(
    // eslint-disable-next-line @typescript-eslint/no-use-before-define -- const thunk defined later in this module
    updateDrawDetailsRedux(response.payload?.drawDetails, isInspection)
  );
  // eslint-disable-next-line @typescript-eslint/no-use-before-define -- const thunk defined later in this module
  dispatch(formatFieldHistoryRedux(response.payload?.drawHistory));
  dispatch(
    updateSelectedBankObj(
      response.payload?.drawDetails?.bankDetails ?? selectedBankDetails
    )
  );
  await refreshBankListAfterSave({ toorakLoanId, owner, isOrig, dispatch });
}

function handleDraftDrawSave(
  dispatch: any,
  response: any,
  selectedBankDetails: any,
  drawId: string
) {
  const draftResponseBankDetails = response.payload?.drawDetails?.bankDetails;
  if (draftResponseBankDetails) {
    dispatch(updateSelectedBankObj(draftResponseBankDetails));
  }
  persistDraftSavedBank(
    drawId,
    draftResponseBankDetails || selectedBankDetails
  );
}

export function getDrawRequestData(
  filterObj: any,
  isOriginator: boolean,
  page: number,
  size: number,
  cb?: any
): any {
  return async (dispatch: any) => {
    try {
      filterObj.offsetTime = new Date().getTimezoneOffset();
      dispatch(showLoader());
      const owner = getCookie("org_id");
      const url = `${apiUrlHost}/drawservice/assetmanagement/draws/all?owner=${owner}&isOriginator=${isOriginator}&page=${page}&size=${size}&isTestParty=${getCookie(
        "isTestParty"
      )}`;
      const response = (await publicClient.post(url, filterObj)).data;
      // let formattedData = [...response.data];
      if (cb) {
        cb(response.totalDraws);
      } else {
        const UpdatedResponse = response.draws.map((item: any) => {
          if (!item.progress) {
            item.progress = " ";
          }
          return item;
        });

        dispatch(
          updateDrawData(UpdatedResponse.length > 0 ? UpdatedResponse : [])
        );
        dispatch(
          updateDrawCount(UpdatedResponse.length > 0 ? response.totalDraws : 0)
        );
      }

      dispatch(hideLoader());
    } catch (err) {
      dispatch(hideLoader());
      console.error(err);
    }
  };
}

export function getAllAssignee(): any {
  return async (dispatch: any) => {
    try {
      const url = `${apiUrlHost}/drawservice/assetmanagement/ammembers`;
      let response = (await publicClient.get(url)).data;
      response = response.filter(
        (item: any) => item.amMemberName.trim() !== ""
      );
      dispatch(updateAssigneeList(response?.length > 0 ? response : []));
    } catch (err) {
      console.error(err);
    }
  };
}

export async function updateAssigneTo(drawIds: any, value: any) {
  try {
    const owner = getCookie("org_id");
    const url = `${apiUrlHost}/drawservice/assetmanagement/assign?owner=${owner}`;
    const reqBody = {
      drawList: drawIds,
      assignedTo: value
    };
    const response = await publicClient.patch(url, reqBody);
    if (response.status === 200) {
      return;
    }
  } catch (err) {
    console.error(err);
    throw err;
  }
  // };
}

export function bulkUpdateStatus(
  status: DrawStage,
  drawId: any,
  role: string,
  comment: string
): any {
  return async (dispatch: any) => {
    try {
      dispatch(showLoader());
      const owner = getCookie("org_id");
      const url = `${apiUrlHost}/drawservice/assetmanagement/draws/update/stage?isOriginator=false&owner=${owner}`;
      const reqBody = {
        stage: status,
        comment,
        role,
        username: getUsername(),
        drawId: drawId.join().split(",")
      };
      const response = await (await publicClient.put(url, reqBody)).data;
      if (response.status === 200) {
        return;
      }
      dispatch(hideLoader());
    } catch (err) {
      dispatch(hideLoader());
      console.error(err);
    }
  };
}

export async function updateDrawDocTagStatus(
  drawId: any,
  dispatch: any,
  statuses: any,
  options?: { silent?: boolean }
) {
  try {
    if (!options?.silent) {
      dispatch(showLoader());
    }
    const url = `${apiUrlHost}/drawservice/assetmanagement/draws/${drawId}/tagstatus`;
    const reqBody: any = {};
    statuses?.forEach((status: any) => {
      if (status.value !== undefined) {
        reqBody[status.key] = status.value;
        reqBody.inspectionReviewApprovedUser =
          status?.inspectionReviewApprovedUser;
        reqBody.inspectionReviewApprovedOn = status?.inspectionReviewApprovedOn;
        reqBody.amountMismatchFlag = status?.amountMismatchFlag;
      }
    });
    await publicClient.patch(url, reqBody);
    if (!options?.silent) {
      dispatch(hideLoader());
    }
  } catch (err) {
    if (!options?.silent) {
      dispatch(hideLoader());
    }
    console.error(err);
  }
}

/**
 * Persist documentTaggingStatus from doc confidence when it differs from the stored value.
 * Only syncs when a primary inspection/ADRF doc is mapped to a property.
 * Used on draw details (docs fetch) so status does not stay Pending until the tagging page is opened.
 */
export async function syncDrawDocumentTaggingStatus(
  drawId: string,
  docs: any[],
  dispatch: any,
  getState: any,
  options?: { silent?: boolean }
) {
  if (!drawId || !docs?.length || isOrigUser()) return;

  const nextStatus = resolveDrawDocumentTaggingStatus(docs);
  if (!nextStatus) return;

  const currentDraw = getState()?.drawRequestStore?.drawDetails;
  const currentStatus = currentDraw?.documentTaggingStatus;
  if (currentStatus === nextStatus) return;

  await updateDrawDocTagStatus(
    drawId,
    dispatch,
    [{ key: "documentTaggingStatus", value: nextStatus }],
    { silent: options?.silent ?? true }
  );

  if (currentDraw) {
    dispatch(
      updateDrawObj({
        ...currentDraw,
        documentTaggingStatus: nextStatus
      })
    );
  }
}

export function getElasticSearchQuery(val: string) {
  return {
    query: {
      bool: {
        should: [
          {
            multi_match: {
              query: val,
              fields: [
                "loan.loanDetailId.toorakLoanId",
                "loan.loanInfo.primaryLoanId"
              ],
              type: "phrase_prefix"
            }
          }
        ]
      }
    },
    source: ["*"],
    trackTotalHits: true,
    sort: [
      {
        fieldPath: "updatedOn",
        order: "DESC"
      }
    ],
    size: 20,
    from: 0
  };
}

export function getProvisionsData(
  filterObj: any,
  isOriginator: boolean,
  page: number,
  size: number
): any {
  return async (dispatch: any) => {
    try {
      filterObj.offsetTime = new Date().getTimezoneOffset();
      dispatch(showLoader());
      const owner = getCookie("org_id");
      const url = `${apiUrlHost}/drawservice/assetmanagement/provisions/all?owner=${owner}&isOriginator=${isOriginator}&page=${page}&size=${size}`;
      const response = (await publicClient.post(url, filterObj)).data;
      dispatch(updateProvisionData(response));
      dispatch(hideLoader());
    } catch (err) {
      dispatch(hideLoader());
      console.error(err);
    }
  };
}

export function addProvision(
  originatorLoanId: string,
  toorakLoanId: string,
  provisionval: string,
  provisionId?: any,
  callback?: any
): any {
  return async (dispatch: any) => {
    try {
      dispatch(showLoader());
      const url = `${apiUrlHost}/drawservice/assetmanagement/provisions`;

      const reqBody: any = [];
      if (provisionId) {
        const editProvisionObj = {
          primaryLoanId: originatorLoanId,
          provision: provisionval,
          addedBy: getUsername(),
          toorakLoanId,
          provisionId
        };
        reqBody.push(editProvisionObj);
      } else {
        const addProvisionObj = {
          primaryLoanId: originatorLoanId,
          provision: provisionval,
          addedBy: getUsername(),
          toorakLoanId
        };
        reqBody.push(addProvisionObj);
      }

      await publicClient.post(url, reqBody);
      if (callback) {
        // History fetch endpoints use toorakLoanId, not primary/originator loan id.
        callback(toorakLoanId);
      }
      dispatch(hideLoader());
    } catch (err) {
      console.log("error occured ", err);
      dispatch(hideLoader());
    }
  };
}

export function deleteProvision(
  provisionId: string,
  callBack: any,
  primaryLoanId?: string
): any {
  return async (dispatch: any) => {
    try {
      dispatch(showLoader());
      const url = `${apiUrlHost}/drawservice/assetmanagement/provisions/${provisionId}`;
      await publicClient.delete(url);
      if (callBack) {
        callBack(primaryLoanId);
      }
      dispatch(hideLoader());
    } catch (err) {
      console.log("error occured ", err);
      dispatch(hideLoader());
    }
  };
}

export function getProvisionHistory(toorakLoanId: string, callback?: any): any {
  return async (_dispatch: any) => {
    try {
      // dispatch(showLoader());
      if (!toorakLoanId) {
        console.error("error occured ", "No proper toorakLoanId");
        return;
      }
      const url = `${apiUrlHost}/drawservice/assetmanagement/v2/${toorakLoanId}/provisions`;
      const response = await publicClient.get(url);
      if (response?.data && callback) {
        callback(response.data, toorakLoanId);
      }
      // dispatch(hideLoader());
    } catch (err) {
      console.error("error occured ", err);
      // dispatch(hideLoader());
    }
  };
}

export function updateProvision(toorakLoanId: string, value: string): any {
  return async (dispatch: any) => {
    try {
      dispatch(showLoader());
      const owner = getCookie("org_id");
      const url = `${apiUrlHost}/drawservice/assetmanagement/v2/provisions?owner=${owner}&isOriginator=false`;
      const reqBody = {
        toorakLoanId,
        provision: value
      };
      await publicClient.put(url, reqBody);
      dispatch(hideLoader());
    } catch (err) {
      console.log("error occured ", err);
      dispatch(hideLoader());
    }
  };
}

export function updateElasticSearchResponse(val: any, callback?: any): any {
  return async (dispatch: any) => {
    try {
      if (val.trim() === "") {
        dispatch(updateElasticSearchBody([]));
        return;
      }
      const url = `${getConfig().apiUrl}/search/v2/${
        getConfig().prefix
      }_loan_aggregate/_search`;
      const reqBody = getElasticSearchQuery(val);
      const response = await publicClient.post(url, reqBody);
      const resArray =
        response?.data?.response?.hits?.hits?.length > 0
          ? response.data.response.hits.hits
          : [];
      dispatch(updateElasticSearchBody(resArray));
      if (callback) {
        callback(resArray);
      }
    } catch (err) {
      console.error(err);
    }
  };
}

export const updateDrawProgressField =
  (drawIds: any, progress: string, callBack?: any): any =>
  async (dispatch: any) => {
    if (progress === "Select") return;
    dispatch(showLoader());
    try {
      const owner = getCookie("org_id");
      const originatorName = getCookie("organizationName");
      const personId = getCookie("person_id");
      const url = `${apiUrlHost}/drawservice/assetmanagement/draws/update/progress?owner=${owner}&isOriginator=false&originatorName=${originatorName}&personId=${personId}`;
      const reqBody = {
        progress,
        drawIds
      };
      const response = (await publicClient.put(url, reqBody)).data;
      if (response) {
        callBack?.();
        dispatch(hideLoader());
      }
    } catch (e) {
      console.log(e);
      dispatch(hideLoader());
    }
  };

let loansRequestInFlight = false;

export function getAvailableLoanIds(isOriginator: boolean): any {
  return async (dispatch: any, getState: any) => {
    if (loansRequestInFlight) return;
    const existingIds = getState?.()?.drawRequestStore?.loanIds;
    if (existingIds?.length) return;
    loansRequestInFlight = true;
    try {
      dispatch(showLoader());
      const owner = getCookie("org_id");
      const isTestParty = getCookie("isTestParty") === "true";
      const url = `${apiUrlHost}/drawservice/assetmanagement/v2/draws/loans?owner=${owner}&isOriginator=${isOriginator}&isTestParty=${isTestParty}`;
      const response = await publicClient.get(url);
      dispatch(updateLoanIds(response.data));
      dispatch(hideLoader());
    } catch (err) {
      dispatch(hideLoader());
      console.error(err);
    } finally {
      loansRequestInFlight = false;
    }
  };
}

export type GetDrawDetailsByLoanIdOptions = {
  isChanged?: boolean;
  drawAmountDetails?: any;
  drawObj?: any;
  isEditing?: boolean;
};

function buildLoanDetailsContext(
  currentState: any,
  response: any,
  originalLoanId: string
) {
  const drawDetails = currentState?.drawRequestStore?.drawDetails;
  const stage = drawDetails?.stage || response?.stage;
  const primaryLoanId = drawDetails?.primaryLoanId || response?.primaryLoanId;
  const shouldPreserve = shouldPreserveDraftLoanDetails({
    stage,
    loanIdParam: originalLoanId,
    drawPrimaryLoanId: drawDetails?.primaryLoanId || primaryLoanId,
    drawToorakLoanId: drawDetails?.toorakLoanId,
    responsePrimaryLoanId: response?.primaryLoanId,
    responseToorakLoanId: response?.toorakLoanId
  });
  const selectedBank = currentState?.drawRequestStore?.selectedBankDetails;
  return {
    shouldPreserve,
    firstLoadBankSnapshot:
      shouldPreserve && selectedBank ? { ...selectedBank } : null,
    fullBankListFromApi: captureBankListFromLoanDetailsResponse(
      response.bankDetails
    )
  };
}

function applyLoanDetailsResponse(params: {
  response: any;
  isChanged: boolean;
  drawAmountDetails: any;
  drawObj: any;
  drawId: string;
  getState: any;
  currentState: any;
  originalLoanId: string;
  dispatch: any;
}) {
  const {
    response,
    isChanged,
    drawAmountDetails,
    drawObj,
    drawId,
    getState,
    currentState,
    originalLoanId,
    dispatch
  } = params;
  const context = buildLoanDetailsContext(
    currentState,
    response,
    originalLoanId
  );
  if (isChanged) {
    applyChangedLoanDetails({
      response,
      drawAmountDetails,
      drawId,
      getState,
      shouldPreserve: context.shouldPreserve,
      currentState,
      drawObj,
      firstLoadBankSnapshot: context.firstLoadBankSnapshot,
      dispatch
    });
  }
  dispatchAvailableBanksAndProps(
    response,
    context.fullBankListFromApi,
    context.shouldPreserve,
    currentState,
    dispatch
  );
  if (context.shouldPreserve) {
    reapplyExcelAmountAndBank(
      drawId,
      getState,
      dispatch,
      context.firstLoadBankSnapshot
    );
  }
}

export function getDrawDetailsbyLoanId(
  isOriginator: boolean,
  toorakLoanId: string,
  drawId: string,
  setSnackBarMsg: any,
  setOpenSnackBar: any,
  options: GetDrawDetailsByLoanIdOptions = {}
): any {
  const {
    isChanged = true,
    drawAmountDetails = {},
    drawObj,
    isEditing
  } = options;
  return async (dispatch: any, getState: any) => {
    try {
      dispatch(showLoader());
      const owner = getCookie("org_id");
      const originalLoanId = toorakLoanId;
      const isTestParty = getCookie("isTestParty") === "true";
      const skipCheck = isEditing ? "&skipActiveDrawCheck=true" : "";
      const url = `${apiUrlHost}/drawservice/assetmanagement/v2/draws/loans/${toorakLoanId}/loan-details?owner=${owner}&isOriginator=${isOriginator}&drawId=${drawId}&isTestParty=${isTestParty}${skipCheck}`;
      const response = (await publicClient.get(url)).data;
      handleLoanDetailsErrorMessage(response, setOpenSnackBar, setSnackBarMsg);
      applyLoanDetailsResponse({
        response,
        isChanged,
        drawAmountDetails,
        drawObj,
        drawId,
        getState,
        currentState: getState(),
        originalLoanId,
        dispatch
      });
      dispatch(hideLoader());
    } catch (err) {
      dispatch(hideLoader());
      console.error(err);
    }
  };
}

export function getDocsListbyDrawId(
  isOriginator: boolean,
  drawId: string
): any {
  return async (dispatch: any, getState: any) => {
    try {
      dispatch(showLoader());
      const owner = getCookie("org_id");
      const url = `${apiUrlHost}/drawservice/assetmanagement/draws/${drawId}/documents/getAll?owner=${owner}&isOriginator=${isOriginator}`;
      const response = (await publicClient.get(url)).data;
      const drawDetailsFromStore = getState()?.drawRequestStore?.drawDetails;
      const autoPrimary = resolveAutoPrimaryInspectionDocs<any>(
        response?.DocumentsList,
        {
          getId: (doc: any) => doc?.documentId,
          getTags: (doc: any) => doc?.tags,
          inspectionReviewStarted: isInspectionReviewStarted(
            drawDetailsFromStore?.budgetReconcileStatus
          ),
          // Multi-doc switching waits for the draw status, else a race can flip
          // the primary the user picked.
          statusLoaded: Boolean(drawDetailsFromStore?.drawId)
        }
      );
      const docList = autoPrimary.docs;
      if (autoPrimary.flagUpdates.length) {
        try {
          await updatePurchaseFlags(autoPrimary.flagUpdates);
        } catch (err) {
          console.error(err);
        }
      }
      dispatch(getDocsByDrawId(docList));
      if (docList?.length) {
        await syncDrawDocumentTaggingStatus(
          drawId,
          docList,
          dispatch,
          getState,
          { silent: true }
        );
      }
      dispatch(hideLoader());
    } catch (err) {
      dispatch(hideLoader());
      console.error(err);
    }
  };
}

export async function isBudgetReviewRedo(
  loanId: string,
  selectedPropId: string,
  dispatch: any
): Promise<any> {
  try {
    dispatch(showLoader());
    const url = `${apiUrlHost}/ocr/budget-loan/${loanId}`;
    const response: any = (await publicClient.get(url)).data;

    const propData = response.budgetDocumentInfoList?.find(
      (item: any) => item.propertyId == selectedPropId
    );

    const propType = getBudgetReviewPropType(response, selectedPropId);
    if (propType) {
      dispatch(setBudgetReviewPropType(propType));
    }

    dispatch(hideLoader());
    return propData?.isRedoInProcess;
  } catch (err) {
    dispatch(hideLoader());
    console.error(err);
    throw err;
  }
}

export async function getInspectionReviewDetails(
  loanId: string,
  drawId: string,
  dispatch: any
) {
  try {
    dispatch(showLoader());
    const url = `${apiUrlHost}/drawservice/assetmanagement/loan/${loanId}/inspectionreview/draw/${drawId}`;
    const response = (await publicClient.get(url)).data;
    dispatch(updateInspectionReviewRedux(response));
    dispatch(hideLoader());
  } catch (err) {
    dispatch(hideLoader());
    console.error(err);
  }
}

export async function getAllBudgetInspectionComments(
  chatIds: any,
  lineItemsList: any,
  dispatch: any
) {
  try {
    dispatch(showLoader());
    const payloadChatIds = [
      ...chatIds.budgetReconcile,
      ...chatIds.budgetReview
    ];
    const url = `${apiUrlHost}/drawservice/assetmanagement/draw/budgetreconcile/comments?limit=200&offset=0`;
    const reqBody = {
      chatIds: payloadChatIds
    };
    const response: any = (await publicClient.post(url, reqBody)).data || [];
    const budgetReviewComments: any = [];
    const budgetReconcileComments: any = [];
    response?.forEach((item: any) => {
      const { chatLines: chatRes, customers } = item;
      const userMap = new Map();
      customers?.forEach((cus: any) => {
        userMap.set(cus.partyId, cus);
      });
      chatRes?.forEach((key: any) => {
        const userObj = userMap.get(key.partyId);
        let userName = "";
        if (userObj?.firstName) {
          userName += `${userObj?.firstName} `;
        }
        if (userObj?.middleName) {
          userName += `${userObj?.middleName} `;
        }
        if (userObj?.lastName) {
          userName += userObj?.lastName;
        }
        key.createdBy = userName;
        const lineItem = lineItemsList.find(
          (line: any) =>
            key.chatId === line.reconcileCommentDiscussionId ||
            key.chatId === line.commentDiscussionId
        );
        key.lineItem = lineItem?.item;
        if (chatIds.budgetReconcile?.includes(key.chatId)) {
          budgetReconcileComments.push(key);
        }
        if (chatIds.budgetReview?.includes(key.chatId)) {
          budgetReviewComments.push(key);
        }
      });
    });
    budgetReviewComments?.forEach((item: any) => {
      item.type = "budgetReview";
    });
    budgetReconcileComments?.forEach((item: any) => {
      item.type = "budgetReconcile";
    });
    dispatch(updateBudgetReviewCommentsRedux(budgetReviewComments));
    dispatch(updateBudgetReconcileCommentsRedux(budgetReconcileComments));
    dispatch(hideLoader());
    return response;
  } catch (err) {
    dispatch(hideLoader());
    throw err;
  }
}

export async function getBudgetReconcileDetails(
  drawId: any,
  dispatch: any,
  isShowLoader = true
) {
  try {
    if (isShowLoader) {
      dispatch(showLoader());
    }
    const url = `${apiUrlHost}/drawservice/assetmanagement/draw/${drawId}/budgetreconcile`;
    let response = (await publicClient.get(url)).data;
    response = response.sort((a: any, b: any) => {
      const aOrder = a.budgetLineItem?.inspectionItemOrder;
      const bOrder = b.budgetLineItem?.inspectionItemOrder;
      if (aOrder === null && bOrder === null) {
        return a.budgetLineItem?.item.localeCompare(b.budgetLineItem?.item);
      }
      if (aOrder === null) {
        return 1;
      }
      if (bOrder === null) {
        return -1;
      }
      return aOrder - bOrder;
    });
    response = formatBudget(response);
    dispatch(updateBudgetReconcileRedux(response));
    if (isShowLoader) {
      dispatch(hideLoader());
    }
    return response;
  } catch (err) {
    if (isShowLoader) {
      dispatch(hideLoader());
    }
    console.error(err);
    return [];
  }
}

export async function redoReconcileHandler(drawId: any, dispatch: any) {
  try {
    dispatch(showLoader());
    const url = `${apiUrlHost}/drawservice/assetmanagement/draw/budgetreconcile/redo/inspectionReview`;
    const payload = {
      drawId
    };
    const response = await publicClient.put(url, payload);
    dispatch(hideLoader());
    return response;
  } catch (err) {
    dispatch(hideLoader());
    console.error(err);
    return [];
  }
}

export async function getBudgetReconcileReports(
  drawId: any,
  toorakLoanId: any,
  dispatch: any
) {
  try {
    dispatch(showLoader());
    const url = `${apiUrlHost}/drawservice/assetmanagement/aggregate/${toorakLoanId}/reports/${drawId}`;
    const response = (await publicClient.get(url)).data;
    dispatch(updateBudgetReconcileReportsRedux(response));
    dispatch(hideLoader());
    return response;
  } catch (err) {
    dispatch(hideLoader());
    console.error(err);
    return {};
  }
}

export function getReconcileItemHistory(
  reconcileItemsId: any,
  dispatch: any
) {
  try {
    dispatch(showLoader());
    dispatch(
      updateReconcileItemHistoryRedux([
        {
          drawNumber: 2,
          amount: 750,
          completion: `50 ${reconcileItemsId}`,
          createdAt: "25-06-2024"
        },
        {
          drawNumber: 1,
          amount: 760,
          completion: "20",
          createdAt: "24-06-2024"
        }
      ])
    );
    dispatch(hideLoader());
  } catch (err) {
    dispatch(hideLoader());
    console.error(err);
  }
}

export async function updateIRPredictionStatus(
  reqBody: any,
  extractionSourceDocumentId: string,
  cb: any
) {
  const url = `${apiUrlHost}/ocr/extraction/${extractionSourceDocumentId}`;
  const resp = (await publicClient.patch(url, reqBody)).data;
  // dispatch(updateInspectionReviewRedux(resp));
  cb?.(resp);
}

export const getBudgetReconcileCommentsList = async (drawId: string) => {
  const comments: any = (
    await getChatMeta("DRAW", drawId, "CHAT_TOPIC", "INSPECTION_REVIEW")
  ).data;
  return comments;
};

export async function updateBudgetReconcileButtonAction(
  drawId: string,
  payload: any,
  dispatch: any
) {
  try {
    dispatch(showLoader());
    const url = `${apiUrlHost}/drawservice/assetmanagement/draw/budgetreconcile/${drawId}/comments`;
    await publicClient.put(url, payload);
    dispatch(hideLoader());
  } catch (err) {
    dispatch(hideLoader());
    console.error(err);
  }
}

export async function updateBudgetApprovedAmount(
  drawId: string,
  payload: any,
  _dispatch: any
) {
  try {
    // dispatch(showLoader());
    const url = `${apiUrlHost}/drawservice/assetmanagement/draw/budgetreconcile/${drawId}/updateApprovedLatestCompletion`;
    await publicClient.patch(url, payload);
    // dispatch(hideLoader());
  } catch (err) {
    // dispatch(hideLoader());
    console.error(err);
  }
}

export async function startReconcileAPI(drawId: string) {
  try {
    const url = `${apiUrlHost}/drawservice/assetmanagement/draw/${drawId}/startreconcile`;
    const response = (await publicClient.put(url)).data;
    return response;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export const getIRandBRHistory = async (reqBody: any) => {
  // return async (dispatch: any) => {
  try {
    const url = `${apiUrlHost}/taskservice/tasks/audit/fetch`;
    const response = (await publicClient.post(url, reqBody)).data;
    const sortedInspectionHistory = sortArrayByUpdatedOn(
      response[0]?.budgetReconcile
    );
    const returnObj: any = {
      sortedInspectionHistory,
      sortedBudgetReconcileHistory: []
    };
    if (response[0]?.inspectionReview) {
      const sortedBudgetReconcileHistory = sortArrayByUpdatedOn(
        response[0].inspectionReview
      );
      returnObj.sortedBudgetReconcileHistory = sortedBudgetReconcileHistory;
      // dispatch(updateBudgetApprovalHistory(sortedBudgetApprovalHistory));
    }

    return returnObj;
  } catch (e) {
    console.error(e);
    return undefined;
  }
};

export const createAndUpdateIRComments = async (
  commentId: string,
  drawId: string,
  commentText: string,
  title: string
) => {
  const { partyId } = getPartyId();
  const payload = {
    partyId,
    lineText: commentText,
    hasDocuments: false
  };
  let commentThreadId;
  try {
    if (commentId) {
      commentThreadId = commentId;
    } else {
      const res: any = await createCommentThread1(
        "DRAW",
        drawId,
        "CHAT_TOPIC",
        "INSPECTION_REVIEW",
        title
      );
      commentThreadId = res.id;
    }
    return await postLineComment(commentThreadId, payload);
  } catch (error) {
    console.error("An error occurred:", error);
    return undefined;
  }
};

const taskNameToStatus: any = {
  budgetReconcile: "budgetReconcileStatus",
  inspectionReview: "inspectionReviewStatus"
};

export async function saveReviewAndReconcileStatus(
  drawId: string,
  payload: any,
  dispatch: any,
  operation?: any
) {
  try {
    dispatch(showLoader());
    const payloadData: any[] = [];
    const { partyId } = getPartyId();
    const firstName = Cookies.get("firstName");
    const lastName = Cookies.get("lastName");
    const statusesArr: any = [];
    payload?.forEach((item: any) => {
      const statusPayload: any = {};
      statusPayload.key = taskNameToStatus[item.name];
      statusPayload.value = item.status;
      statusesArr.push({
        ...statusPayload,
        inspectionReviewApprovedUser: getCookie("person_id"),
        inspectionReviewApprovedOn: new Date().toISOString(),
        amountMismatchFlag: item?.amountMismatchFlag
      });
      payloadData.push({
        resourceId: drawId,
        resourceType: "DRAW",
        assignedTasks: [
          {
            taskStatus: item.status,
            assignedBy: `${firstName} ${lastName}`,
            taskName: item.name,
            resourceId: item.resourceId || drawId,
            taskId: operation !== "add" && item.taskId,
            op: operation || "replace",
            updatedBy: partyId,
            comment: item.comment
          }
        ]
      });
    });
    await updateDrawDocTagStatus(drawId, dispatch, statusesArr);
    const url = `${apiUrlHost}/taskservice/tasks`;
    const response = (await publicClient.patch(url, payloadData)).data;
    dispatch(hideLoader());
    return response;
  } catch (err) {
    dispatch(hideLoader());
    console.error(err);
    throw err;
  }
}

export async function updateInspectionReview(
  drawId: string,
  reqBody: any,
  dispatch: any
) {
  try {
    const url = `${apiUrlHost}/drawservice/assetmanagement/draw/${drawId}/inspectionreview`;
    const response = (await publicClient.patch(url, reqBody)).data;
    dispatch(updateInspectionReviewRedux(reqBody));
    return response;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export const postDrawStageHistory: any =
  (
    drawId: string,
    stage: DrawStage,
    role: string,
    createdBy: string,
    comment = ""
  ) =>
  async (dispatch: any) => {
    dispatch(showLoader());
    try {
      const owner = getCookie("org_id");
      const originatorName = getCookie("organizationName");
      const personId = getCookie("person_id");
      const url = `${apiUrlHost}/drawservice/assetmanagement/draws/${drawId}/update/stage?owner=${owner}&isOriginator=false&originatorName=${originatorName}&personId=${personId}`;

      const reqBody = {
        stage,
        comment,
        role,
        username: getUsername(),
        createdBy
      };
      const response = (await publicClient.put(url, reqBody)).data;
      if (response) {
        dispatch(updateDrawStageHistory(response.payload?.drawHistory));
        dispatch(hideLoader());
        return "success";
      }
      dispatch(hideLoader());
      return undefined;
    } catch (e) {
      console.error(e);
      dispatch(hideLoader());
      throw new Error("fail", { cause: e });
    }
  };

export type UpdateDrawDetailsOptions = {
  showLoader?: boolean;
  isInspection?: boolean;
};

export const updateDrawDetails =
  (
    drawObj: any,
    selectedBankDetails: any,
    drawId: string,
    isOrig: boolean,
    Stage: DrawStage,
    createdBy: string,
    options: UpdateDrawDetailsOptions = {}
  ): any =>
  async (dispatch: any) => {
    const showL = options.showLoader ?? true;
    const isInspection = options.isInspection;
    if (showL) {
      dispatch(showLoader());
    }
    const { toorakLoanId, drawAmountDetails, originatorName: origName } = drawObj;
    const {
      costOfInspection,
      drawAmount,
      netFundsToBorrower,
      titleServiceFee,
      wireFee,
      wireReturnFee
    } = drawAmountDetails;
    const owner = getCookie("org_id");
    const personId = getCookie("person_id");
    const reviewStatus = await fetchBudgetReviewStatusIfNeeded(
      Stage,
      drawObj,
      toorakLoanId
    );
    const url = `${apiUrlHost}/drawservice/assetmanagement/draws/${drawId}?isOriginator=${isOrig}&owner=${owner}&originatorName=${origName}&personId=${personId}&createdBy=${createdBy}`;
    const recalculatedNetFunds = calculateNetAmount({
      drawAmount,
      wireFee,
      costOfInspection,
      titleServiceFee,
      wireReturnFee
    });
    dispatch(
      updateDrawObj({
        ...drawObj,
        drawAmountDetails: {
          ...drawAmountDetails,
          netFundsToBorrower: recalculatedNetFunds || netFundsToBorrower
        }
      })
    );
    const drawSaveObject = buildDrawSaveObject({
      drawObj,
      selectedBankDetails,
      Stage,
      isOrig,
      reviewStatus,
      recalculatedNetFunds,
      drawAmountDetails
    });
    assertNetFundsForSubmit(
      drawSaveObject,
      Stage,
      netFundsToBorrower,
      dispatch
    );
    try {
      const response = (await publicClient.put(url, drawSaveObject)).data;
      if (Stage !== DrawStage.draft) {
        await handleNonDraftDrawSave({
          dispatch,
          response,
          isInspection,
          selectedBankDetails,
          toorakLoanId,
          owner,
          isOrig
        });
      } else {
        handleDraftDrawSave(dispatch, response, selectedBankDetails, drawId);
      }
      sessionStorage.removeItem("newDrawCreated");
      dispatch(hideLoader());
      return "success";
    } catch (e) {
      console.error(e);
      dispatch(hideLoader());
      throw new Error("fail", { cause: e });
    }
  };

export function generateDrawId() {
  return new Promise((resolve, reject) => {
    const owner = getCookie("org_id");
    const isOriginator = isOrigUser();
    const username = getUsername();
    const url = `${apiUrlHost}/drawservice/assetmanagement/draws/create?owner=${owner}&isOriginator=${isOriginator}&username=${username}`;
    publicClient
      .get(url)
      .then((res: any) => {
        resolve(res);
      })
      .catch(reject);
  });
}

export function deleteDrawById(drawId: string) {
  return new Promise((resolve, reject) => {
    const owner = getCookie("org_id");
    const isOriginator = isOrigUser();
    const url = `${apiUrlHost}/drawservice/assetmanagement/draws/${drawId}/delete?owner=${owner}&isOriginator=${isOriginator}`;
    publicClient
      .get(url)
      .then((res: any) => {
        resolve(res);
      })
      .catch(reject);
  });
}

export function exportDrawDetails(
  selectedLoans: any,
  isOrig: boolean,
  createdBy: string
): any {
  return async (dispatch: any) => {
    try {
      dispatch(showLoader());
      const tempObj = {
        drawId: selectedLoans
      };
      const owner = getCookie("org_id");
      const personId = getCookie("person_id");
      const url = `${apiUrlHost}/drawservice/assetmanagement/draws/download?owner=${owner}&isOriginator=${isOrig}&personId=${personId}&createdBy=${createdBy}`;
      const response = await publicClient.post(url, tempObj);
      if (response) {
        downloadExportedTape(response.data);
        setTimeout(() => {
          dispatch(hideLoader());
        }, 300);
      }
    } catch (err) {
      console.error(err);
      dispatch(hideLoader());
    }
  };
}

export async function reTriggerExtraction(
  toorakLoanId: string,
  drawId: string,
  cb?: any
): Promise<any> {
  try {
    const url = `${apiUrlHost}/ocr/extraction/retrigger/${toorakLoanId}/documents?drawId=${drawId}&tags=DDDC`;
    const response: any[] = (await publicClient.get(url)).data;
    cb?.(response);
  } catch (err) {
    console.error(err);
  }
}

export function exportDiscussions(
  drawDiscussions: any,
  drawId: any,
  isOrig: boolean,
  tempDiscussion: any[],
  createdBy: string
): any {
  return async (dispatch: any) => {
    try {
      dispatch(showLoader());
      const owner = getCookie("org_id");
      const personId = getCookie("person_id");
      const url = `${apiUrlHost}/drawservice/assetmanagement/draws/${drawId}/discussions?owner=${owner}&isOriginator=${isOrig}&personId=${personId}&createdBy=${createdBy}`;
      const response = (await publicClient.post(url, drawDiscussions)).data;
      if (response) {
        tempDiscussion[drawId].discussions.unshift(response);
        // console.log(tempDiscussion);
        dispatch(updateDrawDiscussions(tempDiscussion));
        dispatch(hideLoader());
      }
    } catch (err) {
      console.error(err);
      dispatch(hideLoader());
    }
  };
}

export const postInternalNotes =
  (
    noteChat: any,
    drawId: any,
    tempInternalNotes: DrawInternalNotesInterface[]
  ): any =>
  async (dispatch: any) => {
    try {
      dispatch(showLoader());
      // const owner = getCookie("org_id");
      // const personId = getCookie("person_id");
      const reqBody = {
        text: noteChat.text,
        role: noteChat.role
      };
      const url = `${apiUrlHost}/drawservice/assetmanagement/${drawId}/internalnote?userName=${noteChat.userName}`;
      const response = (await publicClient.post(url, reqBody)).data;

      if (response) {
        tempInternalNotes.unshift(response);
        dispatch(updateDrawInternalNotes(tempInternalNotes));
      }
      dispatch(hideLoader());
    } catch (err) {
      console.error(err);
      dispatch(hideLoader());
    }
  };

export const getInternalNotes: any =
  (drawId: string) => async (dispatch: any) => {
    try {
      const url = `${apiUrlHost}/drawservice/assetmanagement/internalnote?drawId=${drawId}`;
      const response = (await publicClient.get(url)).data;
      const responseRev = [...response].reverse();

      if (response) {
        dispatch(updateDrawInternalNotes(responseRev));
      }
    } catch (e) {
      console.error(e);
    }
  };

export const insertDrawDocs =
  (body: any, drawId: string, isOrig: boolean, createdBy: string): any =>
  async (_dispatch: any) => {
    const owner = getCookie("org_id");
    const personId = getCookie("person_id");
    const url = `${apiUrlHost}/drawservice/assetmanagement/draws/${drawId}/documents?owner=${owner}&isOriginator=${isOrig}&personId=${personId}&createdBy=${createdBy}`;
    try {
      const response = await publicClient.post(url, body);
      return response;
    } catch (e) {
      console.error(e);
      throw e;
      // upload is not success
    }
  };

const formatFieldHistoryRedux =
  (resp: any, isStage = false) =>
  (dispatch: any) => {
    let newds = {};
    Object.keys(resp).forEach((section: any) => {
      if (
        ["dramAmountFieldEditHistory", "bankDetailsFieldEditHistory"].includes(
          section
        )
      ) {
        resp[section].forEach((item: any) => {
          const fieldName = item.key;
          let fields =
            item.fields.length > 3 ? item.fields.slice(0, 3) : item.fields;
          fields.forEach((field: any) => {
            if (section === "dramAmountFieldEditHistory") {
              field.preValue = sanitizeCurrency(field.preValue);
              field.value = sanitizeCurrency(field.value);
            }
          });
          fields = fields.filter(
            (field: any) => field?.preValue !== "" && field?.preValue !== "0"
          );
          const foo = { [fieldName]: fields };
          newds = { ...newds, ...foo };
        });
      }
    });
    dispatch(setFieldsHistory(newds));
    if (isStage) {
      dispatch(updateDrawStageHistory(resp.stageHistory));
    }
  };

export const getFieldHistory =
  (drawId: string, isOrig: boolean): any =>
  async (dispatch: any) => {
    const owner = getCookie("org_id");

    const url = `${apiUrlHost}/drawservice/assetmanagement/draws/${drawId}/history/all?owner=${owner}&isOriginator=${isOrig}`;
    try {
      const resp = (await publicClient.get(url)).data;
      // do mapping here, if required
      // let newds = {};
      dispatch(formatFieldHistoryRedux(resp, true));
    } catch (e) {
      console.error(e);
    }

    // .then((res: any) => resolve(res))
    // .catch(reject);
  };

export const downloadDrawDocs = (
  docIds: string[],
  drawId: string,
  isOrig: boolean,
  createdBy: string
): Promise<string> =>
  new Promise((resolve, reject) => {
    if (!docIds.length) {
      reject(new Error("No DocIds provided"));
      return;
    }
    const owner = getCookie("org_id");
    const personId = getCookie("person_id");
    const url = `${apiUrlHost}/drawservice/assetmanagement/draws/${drawId}/documents/download?owner=${owner}&isOriginator=${isOrig}&personId=${personId}&createdBy=${createdBy}`;

    const body = {
      docId: docIds
    };

    publicClient
      .post(url, body)
      .then((res: any) => resolve(res))
      .catch(reject);
  });

export const deleteDrawDocs =
  (
    docIds: string[],
    drawId: string,
    isOrig: boolean,
    dontCallGetDocs?: boolean
  ): any =>
  async (dispatch: any) => {
    dispatch(showLoader());
    const owner = getCookie("org_id");

    const url = `${apiUrlHost}/drawservice/assetmanagement/draws/${drawId}/document?owner=${owner}&isOriginator=${isOrig}`;

    try {
      const response = await publicClient.delete(url, {
        data: { docId: docIds }
      });
      if (!dontCallGetDocs) {
        dispatch(getDocsByDrawId(response.data.payload?.drawDocuments));
      }
      dispatch(hideLoader());
      return response;
    } catch (e) {
      console.error(e);
      throw e;
      // upload is not success
    }
  };

const updateDrawDetailsRedux =
  (response: any, isInspection?: boolean, drawId?: string) =>
  async (dispatch: any) => {
    dispatch(showLoader());
    dispatch(updateDrawObj(response));
    const bankPayload = resolveBankPayloadWithSession(response, drawId);
    dispatch(
      updateSelectedBankObj(
        mergeBankDecryptMetadata(bankPayload, apiBankFromResponse(response))
      )
    );
    dispatchPropertyAndBankList(response, dispatch);
    if (response.finalApprovalNotRequired) {
      dispatch(
        updateFinalApproval(response.finalApprovalNotRequired.toLowerCase())
      );
    }
    const filDocsList = await applyAutoPrimaryAndFilterDocs(
      response,
      isInspection
    );
    dispatch(getDocsByDrawId(filDocsList));
    dispatch(updateDrawDiscussions(response.drawDiscussions));
    dispatch(hideLoader());
  };

export function getDrawDetailsByDrawId(
  drawId: string,
  isOrig: boolean,
  isInspection?: boolean,
  updateNetFund?: boolean
): any {
  return async (dispatch: any, getState: any) => {
    try {
      dispatch(showLoader());
      const owner = getCookie("org_id");
      const url = `${apiUrlHost}/drawservice/assetmanagement/draws/${drawId}?owner=${owner}&isOriginator=${isOrig}`;
      const response = (
        await publicClient.get(url, {
          errorConfig: getErrorConfigObject({ skipForStatus: [403, 404] })
        } as any)
      ).data;
      if (response) {
        if (updateNetFund) applyDraftNetFunds(response);
        response.drawId = response.drawId ?? drawId;
        preservePropertyDetails(response, getState?.());
        applyExcelMergeToDrawDetailsResponse(response, drawId, getState);
        dispatch(updateDrawDetailsRedux(response, isInspection, drawId));
        dispatch(hideLoader());
      }
    } catch (err: any) {
      console.error(err);
      dispatch(hideLoader());
      if (isDrawAccessDeniedError(err)) {
        throw err;
      }
    }
  };
}
export const getDrawHistory = (toorakLoanId: string): any => {
  const isOrig: boolean = isOrigUser();
  if (isOrig) {
    return () => undefined;
  }
  return async (dispatch: any) => {
    // clearning previous data; AM-276
    dispatch(updateDrawHistory([]));
    const owner = getCookie("org_id");
    const url = `${apiUrlHost}/drawservice/assetmanagement/v2/draws/${toorakLoanId}/draw-history?owner=${owner}&isOriginator=${isOrig}`;

    try {
      // Fetch data
      const resp = await publicClient.get(url);

      // Dispatch action to update draw history with fetched data
      dispatch(updateDrawHistory(resp.data));
    } catch (error) {
      // Handle error, if needed
      console.error("Error fetching draw history:", error);
    }
  };
};

export function updateIsFinalApprovalRequired(
  flag: string,
  drawId: string,
  isOrig: boolean
): any {
  return async (dispatch: any) => {
    const owner = getCookie("org_id");
    dispatch(showLoader());
    if (flag === "yes") {
      const url = `${apiUrlHost}/drawservice/assetmanagement/draws/${drawId}/setFinalApprovalNotRequired?owner=${owner}&isOriginator=${isOrig}`;
      await publicClient.get(url);
      dispatch(hideLoader());
    } else {
      const url = `${apiUrlHost}/drawservice/assetmanagement/draws/${drawId}/unsetFinalApprovalNotRequired?owner=${owner}&isOriginator=${isOrig}`;
      await publicClient.get(url);
      dispatch(hideLoader());
    }
    dispatch(updateFinalApproval(flag));
  };
}

export function createDrawFromExcel(cloudFilePathBucket: any): Promise<any> {
  const username = getUsername();
  const isOriginator = isOrigUser();
  const requestBody: any = {
    owner: getCookie("org_id"),
    isTestParty: getCookie("isTestParty"),
    isOriginator,
    cloudFilePath: {
      BucketName: cloudFilePathBucket.cloudFilePathBucket.BucketName,
      Bucketkey: cloudFilePathBucket.cloudFilePathBucket.Bucketkey
    },
    username
  };
  const url = `${getConfig().apiUrl}/aggregate/extractdraw/BulkDrawUpload`;
  return new Promise((resolve, reject) => {
    publicClient
      .post(url, requestBody)
      .then((res: any) => resolve(res))
      .catch(reject);
  });
}

export function getFilterOption(key: string) {
  const owner = getCookie("org_id");
  const isOriginator = isOrigUser();
  const url = `${
    getConfig().apiUrl
  }/drawservice/assetmanagement/draws/getValuesForColumn/${key}?owner=${owner}&isOriginator=${isOriginator}&isTestParty=${getCookie(
    "isTestParty"
  )}`;
  return publicClient.get(url);
}

export function deleteBankDetailsById(
  bankDetailId: number,
  drawObj: DrawObj
): any {
  return async (dispatch: any) => {
    try {
      dispatch(showLoader());
      const { borrowerEntityName, originatorId, primaryLoanId } = drawObj;
      const owner = getCookie("org_id");
      const isOriginator = isOrigUser();
      const url = `${apiUrlHost}/drawservice/assetmanagement/bankdetails/${bankDetailId}?owner=${owner}&isOriginator=${isOriginator}`;
      const response: any = bankDetailId
        ? await publicClient.delete(url, {
            data: { borrowerEntityName, originatorId, loanId: primaryLoanId }
          })
        : [];
      dispatch(updateBankList(response.data));
      // if (response?.data?.bankList?.length)
      dispatch(hideLoader());
    } catch (err) {
      dispatch(hideLoader());
      console.error(err);
    }
  };
}

/**
 * Update wire verification status for a draw
 * @param drawId - The draw ID
 * @param newStatus - "Pending" | "Completed"
 * @param comment - Optional comment for the status change
 */
export function patchWireVerificationStatus(
  drawId: string,
  newStatus: "Pending" | "Completed",
  comment?: string
): any {
  return async (dispatch: any, getState: any) => {
    try {
      dispatch(showLoader());
      const username = getUsername();
      const owner = getCookie("org_id");
      const isOriginator = isOrigUser();
      const url = `${apiUrlHost}/drawservice/assetmanagement/draws/${drawId}/wire-verification-status?owner=${owner}&isOriginator=${isOriginator}&username=${username}`;
      const reqBody = {
        newStatus,
        comment
      };
      const response = (await publicClient.patch(url, reqBody)).data;
      
      // Update the draw object in Redux with the new wire verification status
      const currentState = getState();
      const currentDraw = currentState?.drawRequestStore?.drawDetails;
      if (currentDraw) {
        dispatch(updateDrawObj({
          ...currentDraw,
          wireVerificationStatus: newStatus
        }));
      }
      
      dispatch(hideLoader());
      return response;
    } catch (err) {
      dispatch(hideLoader());
      console.error("Error updating wire verification status:", err);
      throw err;
    }
  };
}

/**
 * Get wire verification history for a draw
 * @param drawId - The draw ID
 */
export async function getWireVerificationHistory(drawId: string): Promise<any[]> {
  try {
    const owner = getCookie("org_id");
    const isOriginator = isOrigUser();
    const url = `${apiUrlHost}/drawservice/assetmanagement/draws/${drawId}/wire-verification-history?owner=${owner}&isOriginator=${isOriginator}`;
    const response = (await publicClient.get(url)).data;
    return response || [];
  } catch (err) {
    console.error("Error fetching wire verification history:", err);
    return [];
  }
}